import type { UseQueryResult } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { EmptyStateCard } from '@/shared/ui/EmptyState'
import type { ProblemCard } from '@/shared/lib/apiTypes'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { profileTone } from '@/shared/lib/profileTone'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { TARGET_ICON } from '@/shared/ui/icons/target/targetIcons'
import { GrayChip } from '@/shared/ui/GrayChip'
import { MatchTile } from '@/public/features/problem/ui/MatchTile'
import { useProblemMatches } from '@/public/features/problem/composables/problemMatches'
import {
  FIT_GRADE_UNIT,
  FIT_GRADE_UNKNOWN,
} from '@/public/features/problem/constants/fitGrade'
import { formatDate } from '@/shared/lib/format'
import { makeLabeler, useCommonCodes } from '@/shared/lib/codes'

const KEYWORD_CHIP_MAX = 3

export function ProblemDetailMobile({ query }: { query: UseQueryResult<ProblemCard> }) {
  const { categoryLabel, targetLabel, isReady } = useCardLabels()
  const p = query.data
  const recommended = useRecommended(p?.id)
  const keywords = p?.keywords ?? []

  return (
    <DefaultLayout mobileTitle="시민 문제" className="pc:hidden flex min-h-[100dvh] flex-col">

      <QueryState isPending={query.isPending || !isReady} error={query.error}>
        {p ? (
          <>
            <article className="layout-body flex flex-col gap-6 p-4">
              <div className="flex flex-col gap-4">
                <h1 className="m-0 text-headline-xs leading-[1.7] font-bold tracking-[-0.1px] text-black-800">
                  {p.title}
                </h1>
                {p.summary ? <p className="m-0 text-body-md-reading text-black-800">{p.summary}</p> : null}
              </div>

              <div className="flex items-center gap-2.5">
                <div className="flex min-w-px flex-1 flex-wrap items-center gap-2.5">
                  {(p.categoryCodes ?? []).map((code) => (
                    <GrayChip key={code} paddingClass="px-[15px]">
                      {categoryLabel(code)}
                    </GrayChip>
                  ))}
                </div>
                <span className="shrink-0 text-body-sm leading-[1.2] text-black-100">
                  {formatDate(p.createdAt)}
                </span>
              </div>

              <MatchTile
                title={p.title}
                tags={(p.categoryCodes ?? []).map((code) => `#${categoryLabel(code)}`)}
                ranks={recommended.map((it) => ({
                  id: it.id,
                  title: it.title,
                  fitGradeLabel: it.fitGradeLabel,
                }))}
              />

              <div className="flex flex-col gap-8 py-[18px]">
                {p.targetCodes.length > 0 && (
                  <section className="flex flex-col gap-3">
                    <h2 className="m-0 text-body-sm font-medium text-black-800">해결 대상</h2>
                    <div className="grid grid-cols-4 gap-3">
                      {p.targetCodes.map((code) => {
                        const Icon = TARGET_ICON[code]
                        return (
                          <span
                            key={code}
                            className="flex min-h-[70px] w-full flex-col items-center justify-center gap-2 rounded-lg px-1 py-1 text-center text-body-md font-medium break-keep text-black-100"
                          >
                            {Icon !== undefined && (
                              <span className="shrink-0" aria-hidden="true">
                                <Icon size={32} />
                              </span>
                            )}
                            {targetLabel(code)}
                          </span>
                        )
                      })}
                    </div>
                  </section>
                )}

                {keywords.length > 0 && (
                  <section className="flex flex-col gap-3">
                    <h2 className="m-0 text-body-sm font-medium text-black-800">AI 추출 키워드</h2>
                    <div className="flex items-center gap-3">
                      {keywords.slice(0, KEYWORD_CHIP_MAX).map((word) => (
                        <span
                          key={word}
                          className="inline-flex shrink-0 items-center justify-center rounded-lg bg-white-200 px-3 py-1 text-body-md font-medium whitespace-nowrap text-black-600"
                        >
                          # {word}
                        </span>
                      ))}
                      {keywords.length > KEYWORD_CHIP_MAX && (
                        <span className="ml-auto inline-flex shrink-0 items-center justify-center rounded-lg border border-white-500 bg-white-50 px-4 py-[3px] text-body-md font-medium whitespace-nowrap text-black-400">
                          +{keywords.length - KEYWORD_CHIP_MAX}
                        </span>
                      )}
                    </div>
                  </section>
                )}
              </div>
            </article>

            <div className="h-2 bg-violet-50 md:hidden" aria-hidden="true" />

            <RecommendedCompanies items={recommended} />
          </>
        ) : null}
      </QueryState>
    </DefaultLayout>
  )
}

function useRecommended(problemCardId: string | undefined): RecommendedItem[] {
  const orgTypeLabel = makeLabeler(useCommonCodes('org_type').data)
  const matches = useProblemMatches(problemCardId)

  return (matches.data?.items ?? []).map((m) => ({
    id: m.solutionCardId,
    title: m.solutionCardTitle,
    summary: m.solutionCardSummary,
    orgId: m.orgId,
    orgName: m.orgName,
    orgType: orgTypeLabel(m.orgTypeCode),
    notice: m.accessCondition,
    fitGrade: m.fitGrade,
    fitGradeLabel: m.fitGradeLabel,
  }))
}

type RecommendedItem = {
  id: string
  title: string
  summary: string | null
  orgId: string | null
  orgName: string
  orgType: string
  notice: string | null
  fitGrade: 'low' | 'mid' | 'high' | null
  fitGradeLabel: string | null
}

function RecommendedCompanies({ items }: { items: RecommendedItem[] }) {
  if (items.length === 0) {
    return (
      <section className="layout-body flex flex-1 flex-col gap-3.5 p-4">
        <h2 className="m-0 shrink-0 text-body-lg leading-[1.4] font-semibold text-black-600">추천 해결 카드</h2>
        <div className="flex flex-1 items-center justify-center">
          <EmptyStateCard
            illust="/illust/empty-org.png"
            title="아직 적합한 해결 카드가 없어요."
            lines={['우리 동네 문제 해결에', '함께할 기업을 기다리고 있어요.']}
          />
        </div>
      </section>
    )
  }

  return (
    <section className="layout-body flex flex-col gap-3.5 p-4">
      <h2 className="m-0 text-body-lg leading-[1.4] font-semibold text-black-600">추천 해결 카드</h2>
      <ol className="m-0 flex list-none flex-col gap-4 p-0">
        {items.map((it) => (
          <li key={it.id}>
            <RecommendedCard item={it} />
          </li>
        ))}
      </ol>
    </section>
  )
}

function RecommendedCard({ item }: { item: RecommendedItem }) {
  const grade = item.fitGrade

  return (
    <Link
      className="ui-pressable flex flex-col gap-5 rounded-2xl border border-white-200 bg-white-50 px-6 py-[22px] text-inherit shadow-[0_0_4px_rgb(0_0_0/8%),0_0_12px_rgb(0_0_0/16%)]"
      to={`/solutions/${item.id}`}
      aria-label={`${item.title} 해결 카드 보기`}
    >
      <span className="flex w-full items-start gap-2">
        <span className="flex min-w-px flex-1 items-center gap-2">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center overflow-clip rounded-[99px] border-[1.25px] border-[rgb(0_0_0/5%)] bg-violet-100 text-black-100 [&>svg]:opacity-50"
            {...(item.orgId !== null ? { style: { background: profileTone(item.orgId) } } : {})}
          >
            <CityIcon size={20} />
          </span>
          <span className="flex min-w-0 flex-col justify-center gap-[3px]">
            <span className="truncate text-body-md-reading font-medium text-black-500">
              {item.orgName}
            </span>
            <span className="truncate text-body-sm font-normal text-black-100">{item.orgType}</span>
          </span>
        </span>

        <span
          className={[
            'flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-[10px] px-3 py-1.5 whitespace-nowrap',
            grade === 'high' ? 'bg-violet-800' : 'bg-white-200',
          ].join(' ')}
        >
          <span
            className={`text-label font-medium ${grade === 'high' ? 'text-white-500' : 'text-black-100'}`}
          >
            {FIT_GRADE_UNIT}
          </span>
          <span
            className={`text-body-sm font-semibold ${grade === 'high' ? 'text-white-50' : 'text-black-600'}`}
          >
            {item.fitGradeLabel ?? FIT_GRADE_UNKNOWN}
          </span>
        </span>
      </span>

      <span className="flex w-full flex-col gap-[18px]">
        <span className="flex w-full flex-col gap-2">
          <span className="w-full text-body-lg-reading font-semibold text-black-800">
            {item.title}
          </span>
          {item.summary !== null && item.summary.length > 0 ? (
            <span className="line-clamp-2 w-full text-body-md-reading font-normal text-black-800">
              {item.summary}
            </span>
          ) : null}
        </span>
        {item.notice ? (
          <span className="flex items-center gap-1 text-body-sm font-normal text-violet-600">
            <InfoIcon />
            <span>{item.notice}</span>
          </span>
        ) : null}

      </span>
    </Link>
  )
}

